import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import SignatureCanvas from 'react-signature-canvas'
import toaster from '../../../Utils/toaster'
import {
  BatchDetail,
  BatchItem,
  ItemDraft,
  defaultDraftFromItem,
  disputeDraftPayload,
} from './bulkVosTypes'
import {
  formatBulkVosCallId,
  formatBulkVosInterpreterId,
  formatBulkVosRecordId,
  formatDueLabel,
  formatPeriodLabel,
  formatBulkVosGridTime,
  normalizeBulkVosTimeForApi,
  maskToken,
} from './bulkVosClientPortalFormat'
import {BulkVosDisputeModal} from './BulkVosDisputeModal'
import {BulkVosVosFormViewIcon} from './BulkVosVosFormViewIcon'
import './bulkVosClientPortal.css'

const API_URL = process.env.REACT_APP_API_URL
const LOGO = `${process.env.PUBLIC_URL}/media/logos/connect_logo_dashboard.svg`

type Props = {
  batch: BatchDetail
  portalToken: string
  tokenForDisplay?: string
  /** Public email link only; hidden when embedded in billing for logged-in users. */
  showPublicHeader?: boolean
  onReload: () => Promise<void>
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

export function BulkVosClientPortalView({
  batch,
  portalToken,
  tokenForDisplay,
  showPublicHeader = false,
  onReload,
}: Props) {
  const readOnly = batch.status !== 0
  const [query, setQuery] = useState('')
  const [modalItemId, setModalItemId] = useState<number | null>(null)
  const [drafts, setDrafts] = useState<Record<number, ItemDraft>>({})
  const [items, setItems] = useState<BatchItem[]>(batch.items)
  const [fullName, setFullName] = useState('')
  const [hasSignature, setHasSignature] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [savingDisputeId, setSavingDisputeId] = useState<number | null>(null)
  const sigRef = useRef<SignatureCanvas>(null)

  useEffect(() => {
    setItems(batch.items)
    const next: Record<number, ItemDraft> = {}
    batch.items.forEach((i) => {
      next[i.id] = defaultDraftFromItem(i)
    })
    setDrafts(next)
  }, [batch])

  const getDraft = useCallback(
    (item: BatchItem) => drafts[item.id] ?? defaultDraftFromItem(item),
    [drafts],
  )

  const patchDraft = (itemId: number, patch: Partial<ItemDraft>) => {
    setDrafts((d) => ({...d, [itemId]: {...d[itemId], ...patch}}))
  }

  const persistDispute = async (
    item: BatchItem,
    draft: ItemDraft,
    isDisputed: boolean,
  ): Promise<boolean> => {
    try {
      const res = await axios.post(
        `${API_URL}/bulk-vos/batch/${portalToken}/items/${item.id}/dispute`,
        disputeDraftPayload(draft, !isDisputed),
      )
      return res.data?.status === 'S'
    } catch {
      toaster('error', 'Unable to save changes')
      return false
    }
  }

  const modalItem = modalItemId != null ? items.find((i) => i.id === modalItemId) ?? null : null

  const openDisputeModal = (item: BatchItem) => {
    setModalItemId(item.id)
  }

  const closeDisputeModal = () => setModalItemId(null)

  const cancelDisputeFlag = async () => {
    if (!modalItem || readOnly) return
    const draft = getDraft(modalItem)
    setSavingDisputeId(modalItem.id)
    const ok = await persistDispute(modalItem, draft, false)
    setSavingDisputeId(null)
    if (!ok) return
    closeDisputeModal()
    await onReload()
  }

  /** One action: mark disputed + save adjusted times/remarks, then refresh from server. */
  const saveDisputeModal = async () => {
    if (!modalItem || readOnly) return
    const draft = getDraft(modalItem)
    setSavingDisputeId(modalItem.id)
    const ok = await persistDispute(modalItem, draft, true)
    setSavingDisputeId(null)
    if (!ok) return
    toaster('success', 'Dispute submitted')
    closeDisputeModal()
    await onReload()
  }

  const disputedCount = items.filter((i) => i.isDisputed).length

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((i) => {
      if (!q) return true
      return [
        i.callId,
        formatBulkVosCallId(i.callId),
        i.requesterName,
        i.interpreterId,
        formatBulkVosInterpreterId(i.interpreterId),
        i.recordId,
        i.assignmentDate,
      ].some((v) => (v || '').toLowerCase().includes(q))
    })
  }, [items, query])

  const displayToken = tokenForDisplay ?? portalToken
  const periodLabel = formatPeriodLabel(batch.servicePeriodStartUtc, batch.servicePeriodEndUtc)
  const dueLabel = formatDueLabel(batch.tokenExpiresAt)
  const canEditPartnerRows = !readOnly

  const nameOk = fullName.trim().length > 1
  const ready = nameOk && hasSignature && !readOnly
  const submitAllLabel =
    disputedCount === 0
      ? 'Submit All'
      : `Submit All (${disputedCount} Disputed Record${disputedCount === 1 ? '' : 's'})`

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API_URL}/bulk-vos/batch/${portalToken}/export`, {
        responseType: 'blob',
      })
      downloadBlob(res.data, 'bulk-vos-appointments.csv')
    } catch {
      toaster('error', 'Download failed')
    }
  }

  const submitAll = async () => {
    if (!ready) return
    const sig = sigRef.current?.toDataURL()
    if (!sig) {
      toaster('error', 'Digital signature is required')
      return
    }
    setSubmitting(true)
    try {
      const disputedItems = items
        .filter((i) => i.isDisputed)
        .map((i) => {
          const d = getDraft(i)
          return {
            itemId: i.id,
            clientStartTime:
              normalizeBulkVosTimeForApi(d.editableStartTime ?? i.startTime) ??
              d.editableStartTime ??
              i.startTime,
            clientEndTime:
              normalizeBulkVosTimeForApi(d.editableEndTime ?? i.endTime) ??
              d.editableEndTime ??
              i.endTime,
            clientMilage: d.editableMilage ?? d.originalMilage,
            clientTravelTimeH: d.editableTravelTimeH ?? d.originalTravelTimeH,
            clientTravelTimeM: d.editableTravelTimeM ?? d.originalTravelTimeM,
            clientRemarks: d.remarks ?? i.remarks,
          }
        })
      await axios.post(`${API_URL}/bulk-vos/submit/${portalToken}`, {
        batchId: batch.batchId,
        fullName: fullName.trim(),
        signatureBase64: sig,
        disputedItems,
      })
      toaster('success', 'Submitted')
      await onReload()
    } catch {
      toaster('error', 'Submit failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onSigEnd = () => {
    setHasSignature(true)
  }

  const clearSig = () => {
    sigRef.current?.clear()
    setHasSignature(false)
  }

  return (
    <>
    <div className={`bvp-root ${showPublicHeader ? '' : 'bvp-root--embedded'}`}>
      {showPublicHeader && (
        <header className='bvp-topbar'>
          <div className='bvp-topbar-brand'>
            <img src={LOGO} alt='Connect' />
            <span className='bvp-topbar-divider' aria-hidden />
            <span className='bvp-topbar-title'>Partner Dispute Management</span>
          </div>
          <div className='bvp-topbar-token'>
            <span>Secure link</span>
            <code>{maskToken(displayToken)}</code>
          </div>
        </header>
      )}

      <div className='bvp-main'>
        <header className='bvp-page-header'>
          <h1 className='bvp-page-title'>Partner Dispute Management</h1>
          <p className='bvp-page-subtitle'>
            Review call timestamps, submit duration adjustments, and authorize billing dispute
            records. Those that are not marked dispute will automatically be approved.
          </p>
          {!readOnly && (periodLabel || dueLabel) && (
            <p className='bvp-page-meta'>
              {periodLabel && <span>Service period: {periodLabel}</span>}
              {periodLabel && dueLabel && <span className='bvp-page-meta-sep'> · </span>}
              {dueLabel && <span>Respond by {dueLabel}</span>}
            </p>
          )}
        </header>

        <div className='bvp-toolbar bvp-toolbar--simple'>
          <div className='bvp-search-wrap'>
            <i className='bi bi-search bvp-search-icon' aria-hidden />
            <input
              className='bvp-search'
              type='text'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Filter by Call ID, Requester, Interpreter.'
            />
          </div>
          <button type='button' className='bvp-btn-download' onClick={() => void downloadCsv()}>
            <i className='bi bi-download' aria-hidden />
            Download
          </button>
        </div>

        <div className='bvp-table-wrap mb-5 mt-3'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-3 gy-3'>
              <thead>
                <tr className='fw-semibold text-muted text-uppercase'>
                  <th className='min-w-80px'>Call ID</th>
                  <th className='min-w-150px'>Requester</th>
                  <th className='min-w-100px'>Record ID</th>
                  <th className='min-w-100px'>Interpreter ID</th>
                  <th className='min-w-120px'>Assignment date</th>
                  <th className='min-w-100px'>Start time</th>
                  <th className='min-w-100px'>End time</th>
                  <th className='min-w-100px'>Duration</th>
                  <th className='min-w-180px'>Remarks</th>
                  <th className='min-w-100px text-end'>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className='text-center text-muted py-10'>
                      No appointments match this view.
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => {
                    const draft = getDraft(item)
                    const disputed = item.isDisputed
                    const startDisplay = formatBulkVosGridTime(
                      disputed ? item.startTime ?? draft.editableStartTime : item.startTime,
                    )
                    const endDisplay = formatBulkVosGridTime(
                      disputed ? item.endTime ?? draft.editableEndTime : item.endTime,
                    )
                    const remarksDisplay = disputed
                      ? item.remarks?.trim() || draft.remarks?.trim() || ''
                      : ''
                    const showEditControl = canEditPartnerRows || (readOnly && disputed)

                    return (
                      <tr key={item.id} className={disputed ? 'bg-light-danger' : undefined}>
                        <td>
                          <div className='d-flex align-items-center gap-2'>
                            <span
                              className={`bvp-row-status-dot ${disputed ? 'bvp-row-status-dot--dispute' : ''}`}
                              aria-hidden
                            />
                            <span className='text-gray-800 d-block fs-6'>
                              {formatBulkVosCallId(item.callId)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {item.requesterName ?? '—'}
                          </span>
                        </td>
                        <td>
                          <span
                            className='text-gray-800 d-block fs-6 text-truncate'
                            style={{maxWidth: 120}}
                            title={item.recordId}
                          >
                            {formatBulkVosRecordId(item.recordId)}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {formatBulkVosInterpreterId(item.interpreterId)}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {item.assignmentDate ?? '—'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`d-block fs-6 ${disputed ? 'text-danger fw-semibold' : 'text-gray-800'}`}
                          >
                            {startDisplay ?? '—'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`d-block fs-6 ${disputed ? 'text-danger fw-semibold' : 'text-gray-800'}`}
                          >
                            {endDisplay ?? '—'}
                          </span>
                        </td>
                        <td>
                          {disputed ? (
                            <span className='badge badge-light-danger fs-6 text-danger fw-semibold px-3 py-2'>
                              {item.duration}
                            </span>
                          ) : (
                            <span className='badge badge-light fs-6 text-gray-700 fw-semibold px-3 py-2'>
                              {item.duration ?? '—'}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>{remarksDisplay || '—'}</span>
                        </td>
                        <td className='text-end'>
                          <div className='bvp-row-action'>
                            <BulkVosVosFormViewIcon item={item} portalToken={portalToken} />
                            {showEditControl ? (
                              <button
                                type='button'
                                className={`bvp-action-btn ${disputed ? 'bvp-action-btn--disputed' : ''}`}
                                aria-label={disputed ? 'View disputed record' : 'Dispute record'}
                                onClick={() => openDisputeModal(item)}
                              >
                                <i className='bi bi-pencil-fill' aria-hidden />
                                {disputed ? 'Disputed' : 'Dispute'}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!readOnly && (
          <section className='bvp-formal-auth' aria-labelledby='bvp-formal-auth-title'>
            <div className='bvp-formal-auth-head'>
              <span className='bvp-formal-auth-head-icon' aria-hidden>
                <i className='bi bi-person-check-fill' />
              </span>
              <div>
                <h2 id='bvp-formal-auth-title' className='bvp-formal-auth-title'>
                  Formal Authorization &amp; Sign-off
                </h2>
                <p className='bvp-formal-auth-lead'>
                  Digital signature and requester credentials required for batch submission.
                </p>
              </div>
            </div>

            <div className='bvp-formal-auth-card'>
              <label className='bvp-formal-auth-label' htmlFor='bvp-formal-full-name'>
                Full NAME
              </label>
              <input
                id='bvp-formal-full-name'
                className='bvp-formal-auth-input'
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete='name'
              />

              <div className='bvp-formal-auth-sig-head'>
                <span className='bvp-formal-auth-sig-label'>
                  <i className='bi bi-pen-fill bvp-formal-auth-pen' aria-hidden />
                  DIGITAL SIGNATURE PAD
                </span>
                <button type='button' className='bvp-formal-auth-clear' onClick={clearSig}>
                  Clear Pad
                </button>
              </div>
              <div className='bvp-formal-auth-sig-box'>
                <SignatureCanvas
                  ref={sigRef}
                  {...({onEnd: onSigEnd} as any)}
                  canvasProps={{
                    className: 'bvp-formal-auth-sig-canvas',
                    style: {
                      position: 'relative',
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      touchAction: 'none',
                      cursor: 'crosshair',
                      backgroundColor: '#ffffff',
                    },
                  }}
                />
              </div>

              <div className='bvp-formal-auth-footer'>
                <button
                  type='button'
                  className={`bvp-formal-auth-submit ${ready && !submitting ? 'bvp-formal-auth-submit--ready' : 'bvp-formal-auth-submit--disabled'}`}
                  disabled={!ready || submitting}
                  onClick={() => void submitAll()}
                >
                  <i className='bi bi-send-fill' aria-hidden />
                  {submitting ? 'Submitting…' : submitAllLabel}
                </button>
              </div>
            </div>
          </section>
        )}

        {showPublicHeader && (
          <p className='bvp-footer-note'>
            Questions about a record? Reply to the email that brought you here and our team will pick
            it up.
          </p>
        )}
      </div>

    </div>

    <BulkVosDisputeModal
      show={modalItem != null}
      item={modalItem}
      draft={modalItem ? getDraft(modalItem) : {}}
      readOnly={readOnly}
      saving={modalItem != null && savingDisputeId === modalItem.id}
      onHide={closeDisputeModal}
      onDraftChange={(patch) => modalItem && patchDraft(modalItem.id, patch)}
      onCancelDispute={() => void cancelDisputeFlag()}
      onSave={() => void saveDisputeModal()}
    />

    </>
  )
}
